<template>
    <div class="w-full">
        <div
            v-if="!selectedFile"
            class="mt-4 border-2 border-dashed border-gray-300 py-12 px-4 rounded-lg text-center relative flex flex-col items-center justify-center"
            :class="{ 'bg-gray-50 border-emerald-500': isDragging }"
            @dragover.prevent="handleDragOver"
            @dragleave.prevent="handleDragLeave"
            @drop.prevent="handleDrop"
            data-node-id="6:501"
        >
            <!-- アップロードアイコン -->
            <div class="relative shrink-0 w-24 h-24 flex items-center justify-center">
                <div class="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center">
                <i class="fa-light fa-arrow-up-from-line text-3xl text-gray-700"></i>
                </div>
            </div>

            <!-- テキスト説明 -->
            <div class="space-y-3 py-6">
                <p class="font-bold text-gray-700">
                    アップロードする動画ファイルをドラッグ＆ドロップしてください。
                </p>
                <div class="flex justify-center">
                <ul class="text-xs text-left list-outside list-disc pl-4 text-gray-700">
                    <li>アップロードできる1ファイルの容量は2GBまでです。</li>
                    <li>m4a、mp3、およびmp4ファイルに対応しています。</li>
                </ul>
                </div>
            </div>

            <!-- ファイル選択ボタン -->
            <button
                class="btn-primary"
                @click="triggerFileInput"
                data-name="regular_button"
            >
                <span class="font-bold text-base text-white text-center whitespace-nowrap leading-6">
                    ファイルを選択する
                </span>
            </button>

            <!-- 隠しファイル入力 -->
            <input
                ref="fileInput"
                type="file"
                class="hidden"
                :accept="acceptedFileTypes"
                @change="handleFileChange"
            />
        </div>
        <!-- ファイル選択後の表示 -->
        <div v-else class="text-center mt-4 space-y-4">
            <div class="px-6 md:px-12 space-y-4">
                <ErrorMessage :message="errorMessage" @clear-message="errorMessage = ''" />

                <div class="w-full">
                    <!-- 高さは max-h のみで抑える。w-full を付けると幅が確定して max-h でのクランプ時に
                         アスペクト比が崩れる（縦長動画で映像が横に潰れる）。幅の溢れ止めは
                         preflight の video { max-width: 100% } が担っている。
                         mx-auto は中央寄せ用。preflight は video を display: block にするため、
                         祖先の text-center では中央に寄らず、縮んだときに左端に寄ってしまう -->
                    <video v-if="videoPreview" controls class="max-h-[40vh] mx-auto">
                        <source :src="videoPreview" type="video/mp4">
                        <div class="alert-error items-center mb-4">
                            <i class="fa-solid fa-triangle-exclamation text-alert"></i>
                            お使いのブラウザはビデオタグをサポートしていません。
                            <button type="button" class="alert-close link-styles--gray" @click="clearErrorMessageModal">
                                <i class="fa-light fa-xmark w-6 h-6" title="Close"></i>
                            </button>
                        </div>
                    </video>
                </div>
                <p class="text-sm bg-gray-100 p-4 rounded-lg break-words"><span class="text-sub">ファイル名：</span>{{ selectedFile.name }}</p>
                <p class="text-sm text-center mx-auto">
                    動画をアップロードしたら、自動的に文字起こしが開始されます。
                </p>
            </div>
        </div>

        <!-- YouTube URL 入力（#343: 重複判定キーとして必須。再アップ時は原則固定、ただし #358: INVALID_ARGUMENT_ERROR は編集可） -->
        <div class="mt-6 w-full">
            <div class="flex items-center gap-2 mb-1">
                <label class="text-sm font-bold text-gray-700" for="youtube-url-input">
                    {{ isYoutubeUrlLocked ? 'この動画のYouTube URL' : 'YouTubeの動画URL' }}
                </label>
                <span v-if="!isYoutubeUrlLocked" class="text-xs font-bold text-alert">必須</span>
            </div>
            <p class="text-xs text-gray-500 mb-2">
                <template v-if="isYoutubeUrlLocked">
                    この動画のYouTube URLは変更できません。別の動画として登録したい場合は、新規アップロードから登録してください。
                </template>
                <template v-else>
                    この動画に対応するYouTube URLを入力してください（公開済み動画との重複確認のために使用します）。
                </template>
            </p>
            <input
                id="youtube-url-input"
                v-model="youtubeUrl"
                type="text"
                class="w-full border border-gray-300 box-border flex flex-col gap-1 items-start px-4 py-2 rounded text-sm text-gray-900 leading-6 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors duration-200 disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
                placeholder="例）https://youtu.be/hQiVYWZPwCk"
                :disabled="isYoutubeUrlLocked"
            />
        </div>

        <!-- アップロード実行ボタン（ファイル選択後のみ表示） -->
        <div v-if="selectedFile" class="flex justify-end border-t mt-6">
            <button
                v-if="!isUploading || errorMessage"
                @click="startUpload"
                class="btn-primary mt-4 px-6"
                :class="canUpload ? 'hover:bg-emerald-700' : 'opacity-50 cursor-not-allowed'"
                :disabled="!canUpload"
            >
                アップロードを開始
            </button>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, defineEmits, defineProps } from 'vue'
import { useStore } from 'vuex';
import axios from 'axios';
import ErrorMessage from "../ErrorMessage.vue";

// プロップス
const props = defineProps({
    acceptedFileTypes: {
        type: String,
        default: '.m4a,.mp3,.mp4,video/*,audio/*'
    },
    maxFileSize: {
        type: Number,
        default: 2 * 1024 * 1024 * 1024 // 2GB in bytes
    },
    video: {
        type: Object,
        default: () => {}
    },
})

// リアクティブデータ
const isDragging = ref(false);
const fileInput = ref(null);
const selectedFile = ref(null);
const videoPreview = ref(null);
const errorMessage = ref("");
const isUploading = ref(false);
const youtubeUrl = ref(props.video?.url_youtube || '');
const store = useStore();
const accessToken = store.state.accessToken;

// #343: 再アップロード経路（既存 video_id を持つ）では YouTube URL を固定する。
// URL 変更は「別動画として登録」に等しく、バックエンドの
// video_id_youtube + user + channel 基準での突合が崩れるため禁止する。
const isReupload = computed(() => !!props.video?.video_id);

// #358: INVALID_ARGUMENT_ERROR は URL 自体が原因の可能性が高いため、再アップ経路でも
// YouTube URL の編集を許可する。それ以外の再アップロード経路では URL を固定する。
const isYoutubeUrlLocked = computed(() => {
    if (!isReupload.value) return false;
    return props.video?.latest_error !== 'GEMINI_API_INVALID_ARGUMENT_ERROR';
});

// #343: YouTube URL は必須。未入力 or 形式不正ならアップロード不可にする。
// バックエンド `app/views/api.py::extract_video_id` と同じパターンを使う。
// 具体的には `watch?v=` は `?` 直後に `v=` が続くケースのみ有効（共有URLの標準形式）。
// 任意パラメータの後ろに v= が来る形式（例: ?feature=x&v=ID）は backend がIDを抽出できないため弾く。
const YOUTUBE_URL_PATTERN = /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/shorts\/)([\w-]+)/;
const canUpload = computed(() => {
    if (!selectedFile.value) return false;
    const trimmed = youtubeUrl.value.trim();
    if (!trimmed) return false;
    return YOUTUBE_URL_PATTERN.test(trimmed);
});

// イベント
const emit = defineEmits(['upload-video:video', 'upload-video:start', 'upload-video:failed']);

// メソッド
const handleDragOver = () => {
    isDragging.value = true
}

const handleDragLeave = () => {
    isDragging.value = false
}

const handleDrop = (event) => {
    isDragging.value = false
    
    const files = event.dataTransfer?.files
    if (files && files.length > 0) {
        const file = files[0]
        validateAndEmitFile(file)
    }
}

const triggerFileInput = () => {
    fileInput.value?.click()
}

const handleFileChange = (event) => {
    const files = event.target.files
    if (files && files.length > 0) {
        const file = files[0]
        validateAndEmitFile(file)
    }
    // ファイル入力をリセット（同じファイルを再度選択可能にする）
    event.target.value = ''
}

const validateAndEmitFile = (file) => {
    // ファイルサイズチェック
    if (file.size > props.maxFileSize) {
        emit('upload-video:failed', `ファイルサイズが2GBを超えています。現在のサイズ: ${formatFileSize(file.size)}`);
        return
    }

    // ファイルタイプチェック（簡易版）
    const fileName = file.name.toLowerCase()
    const acceptedExtensions = ['.m4a', '.mp3', '.mp4']
    const hasValidExtension = acceptedExtensions.some(ext => fileName.endsWith(ext))
    
    if (!hasValidExtension && !file.type.startsWith('video/') && !file.type.startsWith('audio/')) {
        // バリデーション失敗通知は親で受信済みの 'upload-video:failed' に集約する
        // （旧 'error' emit は親で未購読のデッドコードだった）
        emit('upload-video:failed', '対応していないファイル形式です。m4a、mp3、mp4ファイルをアップロードしてください。')
        return
    }

    // ファイルタイプがMP4で、サイズも2GB以下の場合
    selectedFile.value = file;
    videoPreview.value = URL.createObjectURL(file);

    // 'fileSelected' emit は親で未購読のため撤去。
    // 後続処理（アップロード実行）は 'upload-video:start' 以降で行う。
}

const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i]
}

const startUpload = async () => {
    if (!selectedFile.value) {
        errorMessage.value = 'ファイルが選択されていません。';
        return;
    }

    if (!canUpload.value) {
        errorMessage.value = 'YouTubeの動画URLを正しく入力してください。';
        return;
    }

    isUploading.value = true;
    let formData = new FormData();
    formData.append('file', selectedFile.value);
    formData.append('youtube_url', youtubeUrl.value.trim());
    // #343: video_id は送らない。再アップロードか否かは YouTube URL の一致で
    // バックエンド側が判定する（video_id_youtube + user + channel 基準）。
    emit("upload-video:start");

    try {
        const apiEndpoint = process.env.VUE_APP_API_ENDPOINT;
        const uploadResponse = await axios.post(`${apiEndpoint}/upload-video/`, formData, {
            headers: {
                'Authorization': `Bearer ${accessToken}`,
                'Content-Type': 'multipart/form-data'
            },
        });
        // アップロード完了
        emit("upload-video:video", uploadResponse.data);

    } catch (error) {
        console.error("アップロードエラー:", error);
        if (error.response?.status == 403) {
            errorMessage.value = 'チャンネルに対するアクセス権がありません。チャンネル管理者にお問い合わせください。';
        } else if (error.response && error.response.data && error.response.data.error) {
            const errorText = error.response.data.error;
            errorMessage.value = errorText;
        } else {
            errorMessage.value = '送信中にエラーが発生しました。時間を空けて再度お試しください。';
        }
        emit('upload-video:failed', errorMessage.value);
    }
}
</script>
